import type { Me } from '@cockpit/shared';
import { AppHeader, Avatar, IconButton, Menu } from '@cockpit/ui';
import { useQueryClient } from '@tanstack/react-query';
import { LogOut, Monitor, Moon, Sun, UserRound } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../odice/logos/logo-horizontal.png';
import logoWhite from '../odice/logos/logo-horizontal-white.png';
import { api } from '../lib/api.js';
import { ROLE_LABEL } from '../lib/labels.js';
import { useIsDark, useTheme, type ThemeChoice } from '../lib/theme.js';

export function Logo({ className = 'h-6 w-auto sm:h-8' }: { className?: string }) {
  const dark = useIsDark();
  return <img src={dark ? logoWhite : logo} alt="Groupe ODICE" className={className} />;
}

function ThemeMenu() {
  const { theme, setTheme } = useTheme();
  const icon = {
    light: <Sun size={20} strokeWidth={1.75} />,
    dark: <Moon size={20} strokeWidth={1.75} />,
    system: <Monitor size={20} strokeWidth={1.75} />,
  };
  const opts: [ThemeChoice, string][] = [
    ['light', 'Clair'],
    ['dark', 'Sombre'],
    ['system', 'Selon le système'],
  ];
  return (
    <Menu
      trigger={(p) => (
        <IconButton label="Thème d’affichage" {...p}>
          {icon[theme]}
        </IconButton>
      )}
      items={opts.map(([k, l]) => ({
        label: l,
        icon: icon[k],
        active: theme === k,
        onSelect: () => setTheme(k),
      }))}
    />
  );
}

export function Layout({ me, children }: { me: Me; children: ReactNode }) {
  const loc = useLocation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const items = [
    {
      key: 'finance',
      label: 'Revue financière',
      href: '/cockpit',
      active: loc.pathname.startsWith('/cockpit'),
    },
    {
      key: 'ops',
      label: 'Revue opérationnelle',
      href: '/revue-operationnelle',
      active: loc.pathname.startsWith('/revue-operationnelle'),
    },
    ...(me.role === 'admin'
      ? [{ key: 'admin', label: 'Administration', href: '/admin', active: loc.pathname.startsWith('/admin') }]
      : []),
  ];
  const logout = async () => {
    await api('/api/auth/logout', { method: 'POST' });
    qc.clear();
    // Derrière Cloudron, la déconnexion passe par la route réservée /logout du proxy.
    if (me.authMode === 'cloudron') window.location.assign('/logout');
    else nav('/connexion');
  };
  return (
    <div className="min-h-screen">
      <a
        href="#contenu"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-sm focus:bg-surface focus:px-3 focus:py-2"
      >
        Aller au contenu
      </a>
      <AppHeader
        logo={
          <Link to="/" aria-label="Accueil du cockpit">
            <Logo />
          </Link>
        }
        nav={items}
        renderLink={(n, className) => (
          <Link key={n.key} to={n.href} className={className} aria-current={n.active ? 'page' : undefined}>
            {n.label}
          </Link>
        )}
        right={
          <>
            <ThemeMenu />
            <Menu
              header={
                <div>
                  <p className="text-sm font-semibold text-ink">{me.name}</p>
                  <p className="text-[12.5px] text-ink-3">{me.email}</p>
                  <p className="mt-1 text-[12px] text-ink-3">{ROLE_LABEL[me.role]}</p>
                </div>
              }
              trigger={(p) => (
                <button type="button" aria-label="Compte" className="ml-1 rounded-pill" {...p}>
                  <Avatar name={me.name} />
                </button>
              )}
              items={[
                {
                  label: 'Mon profil',
                  icon: <UserRound size={16} strokeWidth={1.75} />,
                  onSelect: () => nav('/profil'),
                },
                { label: 'Se déconnecter', icon: <LogOut size={16} strokeWidth={1.75} />, onSelect: logout },
              ]}
            />
          </>
        }
      />
      <main id="contenu" className="mx-auto max-w-page px-4 pb-20 pt-8 sm:px-8">
        {children}
      </main>
    </div>
  );
}

/** En-tête de page : surtitre, titre, sous-titre, zone d'actions. */
export function PageHeader({
  eyebrow,
  title,
  sub,
  aside,
}: {
  eyebrow: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <p className="font-display text-[12px] uppercase tracking-[0.18em] text-ink-3">{eyebrow}</p>
        <h1 className="mt-1 font-display text-[30px] font-bold leading-tight text-ink sm:text-[34px]">
          {title}
        </h1>
        {sub && <p className="mt-1.5 text-[15px] text-ink-2">{sub}</p>}
      </div>
      {aside && <div className="flex flex-wrap items-center gap-2">{aside}</div>}
    </div>
  );
}
